import { Component, computed, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  IndividualInfoResponse,
  IndividualsClient,
  ISetPasswordCommand,
  IUpdateIndividualInfoCommand,
  LoginUsersClient,
  SetPasswordCommand,
  UpdateIndividualInfoCommand,
} from '../../../infrastructure/shared/model/fss.api';
import { ControlsOf } from '../../../infrastructure/shared/forms/forms-common';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../infrastructure/shared/ui/field/field';
import { TextInput } from '../../../infrastructure/shared/ui/text-input/text-input';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Mail01 } from '../../../infrastructure/shared/icons/mail-01/mail-01';
import { Phone } from '../../../infrastructure/shared/icons/phone/phone';
import { HomeLine } from '../../../infrastructure/shared/icons/home-line/home-line';
import { MarkerPin01 } from '../../../infrastructure/shared/icons/marker-pin-01/marker-pin-01';
import { Map01 } from '../../../infrastructure/shared/icons/map-01/map-01';
import { Lock03 } from '../../../infrastructure/shared/icons/lock-03/lock-03';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';

type SettingsSection = 'basic' | 'contact' | 'password';

interface SectionItem {
  key: SettingsSection;
  label: string;
}

const SECTIONS: SectionItem[] = [
  { key: 'basic', label: 'Osnovni podaci' },
  { key: 'contact', label: 'Kontakt informacije' },
  { key: 'password', label: 'Lozinka' },
];

const required = {
  nonNullable: true as const,
  validators: [Validators.required],
};

function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPwd = group.get('newPassword')?.value;
  const confirm = group.get('confirmPassword')?.value;
  if (!newPwd || !confirm) return null;
  return newPwd === confirm ? null : { passwordsMismatch: true };
}

import { PasswordRequirements } from '../../../infrastructure/shared/components/password-requirements/password-requirements';

@Component({
  standalone: true,
  selector: 'app-individual-settings',
  imports: [
    PasswordRequirements,
    PageComponent,
    ButtonComponent,
    FieldComponent,
    TextInput,
    UserCircle,
    Mail01,
    Phone,
    HomeLine,
    MarkerPin01,
    Map01,
    Lock03,
  ],
  templateUrl: './individual-settings.html',
  styleUrl: './individual-settings.scss',
})
export class IndividualSettings {
  private readonly _fb = inject(FormBuilder);
  private readonly _individuals = inject(IndividualsClient);
  private readonly _loginUsers = inject(LoginUsersClient);
  private readonly _alert = inject(AlertService);
  private readonly _loader = inject(LoaderService);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly SECTIONS = SECTIONS;

  protected activeSection = signal<SettingsSection>('basic');
  protected info = signal<IndividualInfoResponse | null>(null);

  protected headerName = computed(() => {
    const i = this.info();
    return [i?.firstName, i?.lastName].filter(Boolean).join(' ');
  });
  protected headerEmail = computed(() => this.info()?.email ?? '');
  protected headerPhone = computed(() => this.info()?.phone ?? '');

  /** Single form covering both Osnovni podaci + Kontakt informacije tabs. */
  protected infoForm = this._fb.group<ControlsOf<IUpdateIndividualInfoCommand>>({
    firstName: new FormControl('', required),
    lastName: new FormControl('', required),
    address: new FormControl('', required),
    city: new FormControl('', required),
    postalCode: new FormControl('', required),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    phone: new FormControl('', required),
  });

  protected passwordForm = this._fb.group<ControlsOf<ISetPasswordCommand>>(
    {
      oldPassword: new FormControl('', required),
      newPassword: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6)],
      }),
      confirmPassword: new FormControl('', required),
    },
    { validators: passwordsMatchValidator },
  );

  constructor() {
    this._loadInfo();
  }

  protected setSection(section: SettingsSection): void {
    this.activeSection.set(section);
  }

  protected isActive(section: SettingsSection): boolean {
    return this.activeSection() === section;
  }

  protected async saveInfo(): Promise<void> {
    this.infoForm.markAllAsTouched();
    if (this.infoForm.invalid) return;

    const cmd = new UpdateIndividualInfoCommand(this.infoForm.getRawValue());

    this._loader.setLoadingText = 'Čuvanje izmena...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._individuals.updateIndividualInfo(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Izmene su uspešno sačuvane.');
      await this._loadInfo();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  protected async savePassword(): Promise<void> {
    this.passwordForm.markAllAsTouched();

    if (this.passwordForm.errors?.['passwordsMismatch']) {
      this._alert.addFailedMsg('Lozinke se ne poklapaju.');
      return;
    }
    if (this.passwordForm.invalid) return;

    const cmd = new SetPasswordCommand(this.passwordForm.getRawValue());

    this._loader.setLoadingText = 'Promena lozinke...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._loginUsers.setPassword(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Izmene su uspešno sačuvane.');
      this.passwordForm.reset();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  private async _loadInfo(): Promise<void> {
    const res = await firstValueFrom(this._individuals.getIndividualInfo());
    const data = res.result;
    if (!data) return;

    this.info.set(data);

    this.infoForm.setValue({
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      address: data.address ?? '',
      city: data.city ?? '',
      postalCode: data.postalCode ?? '',
      email: data.email ?? '',
      phone: data.phone ?? '',
    });
  }
}
